import { useEffect, useState } from 'react';
import { useViewer } from '../store/context';
import type { ThemePref } from '../store/viewer-store';
import { Logo } from './Logo';
import { ModeToggle } from './ModeToggle';
import { BetaBadge } from './BetaBadge';

const THEME_LABEL: Record<ThemePref, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

const OVERFLOW_MENU_ID = 'mr-overflow-menu';

// Below this width the secondary actions collapse into the overflow menu so the
// header stays usable on phones. Matches Tailwind's `sm` breakpoint (640px).
function useCompact() {
  const [compact, setCompact] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(max-width: 639px)');
    const on = () => {
      setCompact(mq.matches);
    };
    mq.addEventListener('change', on);
    return () => {
      mq.removeEventListener('change', on);
    };
  }, []);
  return compact;
}

// A panel/sidebar glyph whose chevron reflects what the click will do: pointing
// in (collapse) when the sidebar is open, pointing out (expand) when it's hidden.
function SidebarToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      {open ? <path d="m16 15-3-3 3-3" /> : <path d="m14 9 3 3-3 3" />}
    </svg>
  );
}

// A column glyph whose width reflects what the click will do: a narrow centered
// column when switching to reading width, a full-bleed frame when switching back.
function ReadingWidthIcon({ narrow }: { narrow: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      {narrow ? (
        <path d="M9 4v16M15 4v16" />
      ) : (
        <path d="M7 8h10M7 12h10M7 16h10" />
      )}
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}

function closeOverflowMenu() {
  if (typeof document === 'undefined') return;
  document.getElementById(OVERFLOW_MENU_ID)?.hidePopover();
}

export function Header({
  onOpenFile,
  onNew,
  onFeedback,
  onReopenDefault,
  onNewWindow,
  scrolled = false,
  activeHeading = null,
  large = false,
  canFind = false,
}: {
  onOpenFile: () => void;
  onNew: () => void;
  onFeedback: () => void;
  onReopenDefault?: () => void;
  onNewWindow?: () => void;
  scrolled?: boolean;
  activeHeading?: string | null;
  large?: boolean;
  canFind?: boolean;
}) {
  const status = useViewer((s) => s.status);
  const docName = useViewer((s) => s.doc?.name ?? null);
  const toc = useViewer((s) => s.toc);
  const themePref = useViewer((s) => s.themePref);
  const zoom = useViewer((s) => s.zoom);
  const sidebarOpen = useViewer((s) => s.sidebarOpen);
  const toggleSidebar = useViewer((s) => s.toggleSidebar);
  const readingWidth = useViewer((s) => s.readingWidth);
  const toggleReadingWidth = useViewer((s) => s.toggleReadingWidth);
  const setThemePref = useViewer((s) => s.setThemePref);
  const zoomIn = useViewer((s) => s.zoomIn);
  const zoomOut = useViewer((s) => s.zoomOut);
  const zoomReset = useViewer((s) => s.zoomReset);
  const setFindOpen = useViewer((s) => s.setFindOpen);
  const editBuffer = useViewer((s) => s.editBuffer);
  const isDirty = useViewer((s) => s.isDirty);
  const isSaving = useViewer((s) => s.isSaving);
  const save = useViewer((s) => s.save);
  const exportAs = useViewer((s) => s.exportAs);

  const compact = useCompact();
  const ready = status === 'ready';
  // We have an editable buffer to persist (any editing mode).
  const editing = ready && editBuffer !== null;
  // The sidebar toggle only makes sense when the document actually has headings.
  const showSidebarToggle = ready && toc.length > 0;
  // "You are here": the active section while reading, the filename near the top,
  // the product name when nothing is open.
  const title = (ready && activeHeading) || docName || 'MarkRoom';

  // Secondary actions, rendered once — inline on desktop, as menu rows on mobile.
  // `inMenu` switches presentation (and closes the menu after acting).
  const renderSecondaryActions = (inMenu: boolean) => {
    const itemClass = inMenu ? 'mr-menu-item' : 'mr-btn';
    const act = (fn: () => void) => () => {
      fn();
      if (inMenu) closeOverflowMenu();
    };

    return (
      <>
        {ready && canFind && (
          <button
            type="button"
            onClick={act(() => {
              setFindOpen(true);
            })}
            aria-label="Find in document"
            className={itemClass}
          >
            Find
          </button>
        )}

        {ready && (
          <div
            className={inMenu ? 'mr-menu-row' : 'flex items-center gap-1'}
            role="group"
            aria-label="Zoom"
          >
            {inMenu && <span className="mr-menu-row__label">Zoom</span>}
            <button type="button" onClick={zoomOut} aria-label="Zoom out" className="mr-btn">
              −
            </button>
            <button
              type="button"
              onClick={zoomReset}
              aria-label="Reset zoom"
              className="mr-btn tabular-nums"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" onClick={zoomIn} aria-label="Zoom in" className="mr-btn">
              +
            </button>
          </div>
        )}

        {ready &&
          (inMenu ? (
            <button
              type="button"
              onClick={act(toggleReadingWidth)}
              aria-pressed={readingWidth}
              className={itemClass}
            >
              Reading width
            </button>
          ) : (
            <button
              type="button"
              onClick={toggleReadingWidth}
              aria-pressed={readingWidth}
              aria-label={readingWidth ? 'Switch to full width' : 'Switch to reading width'}
              title={readingWidth ? 'Switch to full width' : 'Switch to reading width'}
              className="mr-btn"
            >
              <ReadingWidthIcon narrow={readingWidth} />
            </button>
          ))}

        <div className={inMenu ? 'mr-menu-row' : 'contents'}>
          {inMenu && (
            <label className="mr-menu-row__label" htmlFor="mr-theme">
              Theme
            </label>
          )}
          {!inMenu && (
            <label className="sr-only" htmlFor="mr-theme">
              Theme
            </label>
          )}
          <select
            id="mr-theme"
            value={themePref}
            onChange={(e) => {
              setThemePref(e.target.value as ThemePref);
            }}
            className={inMenu ? 'mr-input ml-auto' : 'mr-btn'}
          >
            {(['light', 'dark', 'system'] as const).map((t) => (
              <option key={t} value={t}>
                {THEME_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        {onReopenDefault && (
          <button
            type="button"
            onClick={act(onReopenDefault)}
            aria-label="Set as default Markdown app"
            title="Set as default Markdown app"
            className={itemClass}
          >
            {inMenu ? 'Set as default app' : '⚙'}
          </button>
        )}

        <button type="button" onClick={act(onFeedback)} aria-label="Send feedback" className={itemClass}>
          Feedback
        </button>

        {ready && (
          <button
            type="button"
            onClick={act(() => void exportAs())}
            aria-label="Export a copy (Save As)"
            className={itemClass}
          >
            {inMenu ? 'Export / Save As…' : 'Export'}
          </button>
        )}

        <button type="button" onClick={act(onNew)} aria-label="New document" className={itemClass}>
          New
        </button>

        {onNewWindow && (
          <button
            type="button"
            onClick={act(onNewWindow)}
            aria-label="New Window"
            className={itemClass}
          >
            New Window
          </button>
        )}
      </>
    );
  };

  return (
    <header
      className={`mr-header flex h-20 shrink-0 items-center gap-2.5 border-b border-neutral-200 bg-neutral-50 px-4 dark:border-neutral-800 dark:bg-neutral-900 ${
        scrolled ? 'mr-header--scrolled' : ''
      }`}
    >
      {showSidebarToggle && (
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? 'Hide contents' : 'Show contents'}
          aria-pressed={sidebarOpen}
          className="mr-btn"
        >
          <SidebarToggleIcon open={sidebarOpen} />
        </button>
      )}

      <Logo size={40} />
      <BetaBadge />

      {/* The filename/section title is web-desktop chrome; on mobile it breaks
          with long names and adds noise, so it's hidden there (the empty slot
          still spaces the controls). Progress / search / settings stay. */}
      <span className="mr-title-slot flex min-w-0 flex-1 items-center gap-1.5 text-[0.9375rem] font-medium text-neutral-700 dark:text-neutral-200">
        {isDirty && (
          <span
            className="size-1.5 shrink-0 rounded-full bg-blue-500"
            title="Unsaved changes"
            aria-label="Unsaved changes"
            role="status"
          />
        )}
        <span key={title} className="mr-title hidden truncate sm:block">
          {title}
        </span>
      </span>

      {ready && <span className="mr-progress" aria-hidden="true" />}

      <div className="ml-auto flex items-center gap-1">
        {ready && <ModeToggle large={large} />}

        {editing && (
          <button
            type="button"
            onClick={() => void save()}
            disabled={isSaving || !isDirty}
            aria-label="Save document"
            title="Save (Ctrl/Cmd-S)"
            className="mr-btn mr-btn--primary"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        )}

        {compact ? (
          <>
            <button
              type="button"
              popoverTarget={OVERFLOW_MENU_ID}
              aria-label="More actions"
              aria-haspopup="menu"
              className="mr-btn"
            >
              <MoreIcon />
            </button>
            <div id={OVERFLOW_MENU_ID} popover="auto" className="mr-menu">
              {renderSecondaryActions(true)}
            </div>
          </>
        ) : (
          renderSecondaryActions(false)
        )}

        <button type="button" onClick={onOpenFile} className="mr-btn mr-btn--primary">
          Open
        </button>
      </div>
    </header>
  );
}
